import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import classNames from "classnames";
import React, { useCallback, useMemo, useState } from "react";
import { useIntl } from "react-intl";
import { Button, Col, Container, Form, Row } from "reactstrap";
import { OPENAI_SOCIAL_FORM } from "../../../../constants/modal.constant";
import { useGlobalCore } from "../../../../cores/globals";
import { useMeCore } from "../../../../cores/me";
import { useOpenAiCore } from "../../../../cores/openAi";
import { useSocialPostCore } from "../../../../cores/socialPost";
import { CheckboxWembii } from "../../CheckboxWembii";
import { InputWembii } from "../../InputWembii";
import { useEmailCore } from "../../../../cores/email";
import "./OpenAiForm.scss";

export default function OpenAiForm({
  emailTemplates,
}: {
  emailTemplates?: boolean;
}) {
  const intl = useIntl();
  const { clear, addText, socialProviders } = useSocialPostCore();
  const {
    templates,
    showTemplates,
    setShowTemplates,
    getSocialTemplate,
    getEmailTemplate,
  } = useOpenAiCore();
  const { company } = useMeCore();
  const { turnOffModal } = useGlobalCore();
  const { setMessage } = useEmailCore();
  const [selected, setSelected] = useState(0);
  const [industry, setIndustry] = useState("");
  const [subject, setSubject] = useState("");
  const [additionalInfo, setAdditionalInfo] = useState("");
  const [emoticons, setEmoticons] = useState(true);
  const [hashtags, setHashtags] = useState(true);

  const onSelecting = useCallback(
    (template) => {
      clear();
      if (emailTemplates) {
        setMessage(template.body);
      } else {
        addText(template.body);
      }
      turnOffModal(OPENAI_SOCIAL_FORM);
    },
    [clear, addText, turnOffModal]
  );

  const handleSubmit = useCallback(
    (e) => {
      e.preventDefault();

      const socialMedias = socialProviders.map((social) => social.name!);
      if (emailTemplates) {
        getEmailTemplate(company?.id!, {
          industry,
          subject,
          emoticons,
          hashtags,
          additionalInfo,
          socialMedias,
        });
      } else {
        getSocialTemplate(company?.id!, {
          industry,
          subject,
          emoticons,
          hashtags,
          additionalInfo,
          socialMedias,
        });
      }
    },
    [
      industry,
      subject,
      emoticons,
      hashtags,
      additionalInfo,
      company,
      socialProviders,
      getSocialTemplate,
      getEmailTemplate,
    ]
  );

  const template = useMemo(
    () => templates[selected] || null,
    [templates, selected]
  );

  const increase = useCallback(() => {
    if (selected < templates.length - 1) {
      setSelected(selected + 1);
    }
  }, [selected, templates]);

  const decrease = useCallback(() => {
    if (selected > 0) {
      setSelected(selected - 1);
    }
  }, [selected]);

  const back = useCallback(() => {
    setShowTemplates(false);
  }, [setShowTemplates]);

  const next = useCallback(() => {
    setShowTemplates(true);
  }, [setShowTemplates]);

  return (
    <Container className="h-100 openai-form" fluid>
      <Row className="h-100">
        <Col xs="12">
          <div
            className={classNames("d-flex flex-column h-100 p-3", {
              "justify-content-between": showTemplates,
            })}
          >
            <div className="form-header">
              <h2 className="h2 d-flex justify-content-center align-items-center mb-4">
                {showTemplates
                  ? intl.formatMessage({ id: "app.socialStory.template" })
                  : intl.formatMessage({ id: "app.openAiForm.title" })}
              </h2>
              {!showTemplates && (
                <p className="openai-subtitle">
                  {intl.formatMessage({ id: "app.openAiForm.subtitle" })}
                </p>
              )}
            </div>

            {showTemplates ? (
              <>
                <div className="template-section">
                  <div className="template-navigation">
                    <button
                      className="nav-button"
                      onClick={decrease}
                      disabled={selected === 0}
                    >
                      <FontAwesomeIcon icon={["fas", "angle-left"]} size="lg" />
                    </button>

                    <div className="template-content">
                      {template ? (
                        <div className="d-flex flex-column align-items-center">
                          <div className="template-text pre-wrap">{template.body}</div>

                          <div className="template-counter text-muted mt-3">
                            {selected + 1} / {templates.length}
                          </div>

                          <Button
                            color="primary"
                            className="mt-3"
                            onClick={() => onSelecting(template)}
                          >
                            {intl.formatMessage({
                              id: "app.socialStory.template.select",
                            })}
                          </Button>
                        </div>
                      ) : (
                        <label className="text-muted">
                          {intl.formatMessage({ id: "app.socialStory.template.empty" })}
                        </label>
                      )}
                    </div>

                    <button
                      className="nav-button"
                      onClick={increase}
                      disabled={selected === templates.length - 1}
                    >
                      <FontAwesomeIcon icon={["fas", "angle-right"]} size="lg" />
                    </button>
                  </div>
                </div>

                <Button
                  className="my-1 mt-3"
                  color="white"
                  block
                  onClick={back}
                >
                  {intl.formatMessage({ id: "app.openAiForm.regenerate" })}
                </Button>
              </>
            ) : (
              <div className="d-flex flex-column form-section">
                <Form onSubmit={handleSubmit}>
                  <div className="form-group">
                    <InputWembii
                      label={intl.formatMessage({ id: "app.openAiForm.industry" })}
                      type="text"
                      name="industry"
                      required
                      placeholder={intl.formatMessage({ id: "app.openAiForm.industry" })}
                      value={industry}
                      onChange={setIndustry}
                    />
                  </div>

                  <div className="form-group">
                    <InputWembii
                      label={intl.formatMessage({
                        id: emailTemplates
                          ? "app.openAiForm.purpose"
                          : "app.openAiForm.topic",
                      })}
                      type="text"
                      name="subject"
                      required
                      placeholder={intl.formatMessage({
                        id: emailTemplates
                          ? "app.openAiForm.purpose"
                          : "app.openAiForm.topic",
                      })}
                      value={subject}
                      onChange={setSubject}
                    />
                  </div>

                  <div className="form-group">
                    <InputWembii
                      label={intl.formatMessage({ id: "app.openAiForm.additionalInfo" })}
                      type="textarea"
                      name="additional"
                      required
                      placeholder={intl.formatMessage({ id: "app.openAiForm.additionalInfo" })}
                      value={additionalInfo}
                      onChange={setAdditionalInfo}
                    />
                  </div>

                  <div className="checkbox-section">
                    <div className="checkbox-item">
                      <CheckboxWembii
                        label={intl.formatMessage({ id: "app.openAiForm.noEmoticons" })}
                        checked={!emoticons}
                        onChange={() => setEmoticons(!emoticons)}
                      />
                    </div>

                    <div className="checkbox-item">
                      <CheckboxWembii
                        label={intl.formatMessage({ id: "app.openAiForm.noHashtags" })}
                        checked={!hashtags}
                        onChange={() => setHashtags(!hashtags)}
                      />
                    </div>
                  </div>

                  <div className="button-section">
                    {templates.length > 0 && (
                      <Button
                        className="my-1 mt-4"
                        color="white"
                        block
                        onClick={next}
                      >
                        {intl.formatMessage({ id: "app.openAiForm.viewLastGenerated" })}
                      </Button>
                    )}
                    <Button type="submit" className="my-1 mt-3" color="primary" block>
                      {intl.formatMessage({ id: "app.openAiForm.generate" })}
                    </Button>
                  </div>
                </Form>
              </div>
            )}
          </div>
        </Col>
      </Row>
    </Container>
  );
}
